import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { ApiErrorCode } from '@weloze/shared';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Field } from '../components/ui';
import { AuthLayout } from './AuthLayout';

export function LoginPage() {
  const { user, loading, login } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const state = location.state as { from?: string; notice?: string } | null;
  // Aviso vindo de outra tela ("senha alterada", "senha definida").
  const notice = state?.notice;

  if (!loading && user) {
    return <Navigate to={state?.from ?? '/'} replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFields({});
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields ?? {});
        // Conta bloqueada não é erro de digitação: limpar a senha só atrapalha
        // quem já está tentando entender por que não entra.
        if (err.code === ApiErrorCode.INVALID_CREDENTIALS) setPassword('');
      } else {
        setError('Erro inesperado. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Acesse seu desempenho"
      subtitle="Consulte seus índices de qualidade e entrega junto à Weloze."
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {notice && !error && <Alert variant="success">{notice}</Alert>}
        {error && <Alert>{error}</Alert>}

        <Field
          label="E-mail"
          name="email"
          type="email"
          autoComplete="username"
          inputMode="email"
          required
          autoFocus
          value={email}
          error={fields.email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="voce@suaempresa.com.br"
        />

        <Field
          label="Senha"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          error={fields.password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <Button type="submit" className="w-full" size="lg" loading={submitting}>
          Entrar
        </Button>

        <div className="pt-1 text-center">
          <Link
            to="/esqueci-senha"
            className="text-sm text-brand-600 underline-offset-4 hover:underline"
          >
            Esqueci minha senha
          </Link>
        </div>
      </form>

      <p className="mt-8 border-t border-slate-200 pt-5 text-xs leading-relaxed text-slate-500">
        O acesso é criado pela Weloze. Para solicitar ou alterar usuário e senha, escreva para{' '}
        <a
          href="mailto:qualidade@weloze.com.br"
          className="font-medium text-brand-600 underline-offset-2 hover:underline"
        >
          qualidade@weloze.com.br
        </a>
        .
      </p>
    </AuthLayout>
  );
}
